import { css } from '@emotion/react';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import type { LatLng } from '~/_shared/types/latLng';
import { useTranslation } from '~/_shared/utils/hooks';
import { useElementDimensions } from '~/_shared/utils/hooks/useElementDimensions';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type ReportDemographicData } from '~/exportReport/exportReportData.helpers';
import {
  ExportReportMapComponent, type ExportReportMapProps,
} from '~/exportReport/map/exportReportMap.component';
import { ExportReportNotesAndReferencesComponent } from '~/exportReport/notesAndReferences/exportReportNotesAndReferences.component';
import { ExportReportSummaryComponent } from '~/exportReport/summary/exportReportSummary.component';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import { noteDefinitions } from '~/exportReport/template/noteDefinitions';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateTableRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateTableRenderer.component';
import sampleDataWatermarkBlack from '../../../../assets/images/sampleAreaWatermark/sampleAreaWatermarkBlack.svg';
import sampleDataWatermarkRed from '../../../../assets/images/sampleAreaWatermark/sampleAreaWatermarkRed.svg';

const tableStyle = css({
  marginTop: 10,
});

const relativePositionStyle = css({ position: 'relative' });

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const reportMapStyle = ({ isSampleReport }: { isSampleReport?: boolean }) => (theme: Theme) => css({
  border: `1px solid ${theme.borderColors.primary}`,
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
  '&:after': {
    display: isSampleReport ? 'block' : 'none',
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    pointerEvents: 'none',
  },
});

const reportRoadmapMapStyle = css({
  '&:after': {
    backgroundImage: `url('${sampleDataWatermarkRed}')`,
  },
});

const reportHybridMapStyle = css({
  '&:after': {
    backgroundImage: `url('${sampleDataWatermarkBlack}')`,
    opacity: .5,
  },
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

type ExportReportTemplateSummaryProps = Readonly<{
  className?: string;
  latLng?: LatLng;
  heading: ReactNode;
  semiHeading?: ReactNode;
  description?: ReactNode;
}>;

type ExportReportTemplateRendererProps = Readonly<{
  template: ReportTemplate;
  dataSourceColumns: ReadonlyArray<ReportTableColumn>;
  summaryProps: ExportReportTemplateSummaryProps;
  onTableRowClick?: (itemIndex: number, rowIndex: number) => void;
  onMapLoaded?: () => void;
  mapProps: Pick<ExportReportMapProps, 'type' | 'center' | 'paths' | 'radiusInMeters'>;
  roadmapProps?: Partial<ExportReportMapProps>;
  hybridMapProps?: Partial<ExportReportMapProps>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicData: (columnId: string, demographicId: string) => ReportDemographicData | null;
  isPreviewMode?: boolean;
  isLoading?: boolean;
  isSampleReport?: boolean;
}>;

const SAMPLE_DATA_FIRST_OFFSET = 110;
const SAMPLE_DATA_REPEAT_DISTANCE = 600;

const sampleDataOverlayStyle = css({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
});

const sampleDataLabelStyle = (theme: Theme) => css({
  color: theme.textColors.danger,
  position: 'absolute',
  opacity: .6,
  left: 0,
  right: 0,
  textAlign: 'center',
  textTransform: 'uppercase',
  fontWeight: 700,
  fontSize: 52,
});

const SampleDataOverlayComponent = () => {
  const [t] = useTranslation();
  const { ref, height = 0 } = useElementDimensions();
  const labelsCount = Math.max(1, Math.ceil((height - SAMPLE_DATA_FIRST_OFFSET) / SAMPLE_DATA_REPEAT_DISTANCE));

  return (
    <div css={sampleDataOverlayStyle} ref={ref}>
      {Array.from({ length: labelsCount }, (_, index) => (
        <div
          key={index}
          css={sampleDataLabelStyle}
          style={{ top: SAMPLE_DATA_FIRST_OFFSET + index * SAMPLE_DATA_REPEAT_DISTANCE }}
        >
          {t('exportReport.sampleReport.watermark')}
        </div>
      ))}
    </div>
  );
};

export const ExportReportTemplateRendererComponent = ({
  template, summaryProps, onTableRowClick, onMapLoaded, mapProps, dataSourceColumns, getDemographicLabel, getDemographicData,
  isPreviewMode, isLoading, roadmapProps, hybridMapProps, isSampleReport,
}: ExportReportTemplateRendererProps) => {
  return (
    <>
      <ExportReportSummaryComponent {...summaryProps} name={template.name} />

      {template.items.map((templateItem, itemIndex) => {
        switch (templateItem.type) {
          case 'map': {
            return (
              <div key={`maps-${itemIndex}`} css={mapsWrapperStyle}>
                <ExportReportMapComponent
                  onMapLoaded={onMapLoaded}
                  id={`map${itemIndex}_roadmap`}
                  mapType="roadmap"
                  css={[reportMapStyle({ isSampleReport }), reportRoadmapMapStyle]}
                  {...mapProps}
                  {...roadmapProps}
                />

                <ExportReportMapComponent
                  id={`map${itemIndex}_hybrid`}
                  mapType="hybrid"
                  css={[reportMapStyle({ isSampleReport }), reportHybridMapStyle]}
                  {...mapProps}
                  {...hybridMapProps}
                />
              </div>
            );
          }

          case 'page-break': {
            return (
              <div key={`page-break-${itemIndex}`} css={pageBreakStyle} />
            );
          }

          case 'line-break': {
            return (
              <div key={`line-break-${itemIndex}`}>
                <br />
              </div>
            );
          }

          case 'title': {
            return (
              <div key={`title-${itemIndex}`} css={tableTitleStyle}>{templateItem.content}</div>
            );
          }

          case 'table': {
            const itemTableColumns: ReadonlyArray<ReportTableColumn> = templateItem.columns
              ? templateItem.columns.map(column => ({
                id: column.id,
                type: 'custom',
                heading: column.label,
                secondaryHeading: '',
              }))
              : dataSourceColumns;

            const commonProps = {
              css: tableStyle,
              tableColumns: itemTableColumns,
              templateTable: templateItem,
              onTableRowClick,
              itemIndex,
              dataSourceColumns,
              isPreviewMode,
              isLoading,
              getDemographicData,
              getDemographicLabel,
              isSampleReport,
            };

            if (templateItem.layout === 'RenderDataInASeparateTable') {
              return (
                <Fragment key={`custom-table-${itemIndex}`}>
                  {dataSourceColumns.map((dataSourceColumn, index) => {
                    return (
                      <div css={relativePositionStyle} key={`data-source-column-${dataSourceColumn.id}-${index}`}>
                        <div css={tableTitleStyle}>{dataSourceColumn.heading} {dataSourceColumn.secondaryHeading}</div>
                        <ExportReportTemplateTableRendererComponent
                          {...commonProps}
                          currentDataSource={dataSourceColumn}
                        />

                        {isSampleReport && <SampleDataOverlayComponent />}

                        <div css={pageBreakStyle} />
                      </div>
                    );
                  })}
                </Fragment>
              );
            }

            return (
              <div css={relativePositionStyle} key={`table-${itemIndex}`}>
                <ExportReportTemplateTableRendererComponent
                  {...commonProps}
                />

                {isSampleReport && <SampleDataOverlayComponent />}
              </div>
            );
          }

          case 'notes-and-references': {
            const notes = (templateItem.notes ?? [])
              .map(({ id }) => noteDefinitions.notes[id])
              .filter(notNullsy);

            const references = (templateItem.references ?? [])
              .map(({ id }) => noteDefinitions.references[id])
              .filter(notNullsy);

            return (
              <ExportReportNotesAndReferencesComponent
                key={`notes-and-references-${itemIndex}`}
                notes={notes}
                references={references}
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
